import { NumberSchema, ObjectSchema, array, boolean, mixed, number, object, string } from 'yup';
import { Status } from '../../models/common';
import { CommunicationType, communicationTypes } from '../../models/fu-unit';
import {
    BaudRateOptions,
    Duplex,
    LanPortSetting,
    ModbusFunctionCode,
    ModbusSetting,
    ParityBit,
    Port,
    PortProtocol,
    RedundancyType,
    SerialPortSetting,
} from '../../models/port';
import { ProtocolType } from '../../models/communication-protocol';
import './../custom-method';

export const portNameLanMax = 16;
export const portNumberMax = 65535;

const enumNumbers = <T extends number>(enumObject: Record<string, string | T>): T[] =>
    Object.values(enumObject).filter((value): value is T => typeof value === 'number');

export function modbusSettingValidator(): ObjectSchema<ModbusSetting> {
    return object({
        id: string().strict().optional(),
        index: number().strict().required(),
        portProtocolId: string().strict().nullable().optional(),
        slaveId: string()
            .strict()
            .matches(/^(?:[1-9A-Fa-f]|[0-9A-Fa-f][1-9A-Fa-f]|[1-9A-Fa-f][0-9A-Fa-f])$/, {
                message: 'slaveId must be a hex value between 01h and FFh',
                excludeEmptyString: true,
            })
            .required(),
        functionCode: number<ModbusFunctionCode>()
            .strict()
            .oneOf(
                enumNumbers<ModbusFunctionCode>(ModbusFunctionCode).filter(
                    code => code !== ModbusFunctionCode.UNDEFINED
                )
            )
            .required(),
        startAddress: number().strict().integer().numberBetween(0, 99999).required(),
        numberOfData: number().strict().integer().numberBetween(1, 2000).required(),
    })
        .noUnknown()
        .strict();
}

export function portProtocolValidator(): ObjectSchema<PortProtocol> {
    return object({
        id: string().strict().optional(),
        protocolId: string().strict().required(),
        extUnitId: string().strict().nullable().optional(),
        ceuUnitId: string().strict().nullable().optional(),
        portIndex: number().strict().integer().min(0).required(),
        communicationProtocol: mixed<PortProtocol['communicationProtocol']>().optional(),
        standardModbus: array(modbusSettingValidator()).optional(),
    })
        .noUnknown()
        .strict() as ObjectSchema<PortProtocol>;
}

export function serialPortSettingValidator(): ObjectSchema<SerialPortSetting> {
    return object({
        baudRate: number().strict().required().min(1),
        parityBit: number<ParityBit>()
            .strict()
            .oneOf(enumNumbers<ParityBit>(ParityBit).filter(bit => bit !== ParityBit.UNDEFINED))
            .required(),
        stopBit: number<1 | 1.5 | 2>().strict().oneOf([1, 1.5, 2]).required(),
        dataBit: number<7 | 8>().strict().oneOf([7, 8]).required(),
        duplex: number<Duplex>()
            .strict()
            .oneOf(enumNumbers<Duplex>(Duplex).filter(duplex => duplex !== Duplex.UNDEFINED))
            .required(),
    })
        .noUnknown()
        .strict();
}

export function lanPortSettingValidator(): ObjectSchema<LanPortSetting> {
    return object({
        localIp: string().isIp().required().strict(),
        remoteIp: string().isIp().required().strict(),
        remotePort: number()
            .strict()
            .nullable()
            .notRequired()
            .integer()
            .numberBetween(1, portNumberMax, true)
            .optional(),
    })
        .noUnknown()
        .strict();
}

export function portValidator(): ObjectSchema<Port> {
    const protocolTypesRequiringPolling = [ProtocolType.POLLING, ProtocolType.RECEIVER, ProtocolType.RESPONDER];

    const requiredWhenProtocolMatches = (schema: NumberSchema<number | undefined>) =>
        schema.when('protocol', {
            is: (protocol?: PortProtocol) =>
                !!protocol && protocolTypesRequiringPolling.includes(protocol?.communicationProtocol?.type),
            then: s => s.required(),
            otherwise: s => s.optional(),
        });

    return object({
        port: number().nonNullable().strict().required(),
        use: number<Status>().strict().oneOf([0, 1]).required(),
        type: mixed<CommunicationType>().strict().oneOf(Object.values(communicationTypes)).required(),
        name: string().strict().nonNullable().optional().notRequired().max(portNameLanMax),
        protocol: portProtocolValidator().optional().default(undefined),
        timeout: requiredWhenProtocolMatches(number().strict().integer().min(0)),
        retry: requiredWhenProtocolMatches(number().strict().integer().min(0)),
        pollingInterval: requiredWhenProtocolMatches(number().strict().integer().min(0)),
        redundancy: boolean().strict().required(),
        redundancyUnitId: string()
            .strict()
            .nullable()
            .when('redundancy', {
                is: true,
                then: schema => schema.required(),
                otherwise: schema => schema.optional(),
            }),
        redundancyPortIndex: number()
            .strict()
            .integer()
            .nullable()
            .min(0)
            .when('redundancy', {
                is: true,
                then: schema => schema.required(),
                otherwise: schema => schema.optional(),
            }),
        redundancyType: number<RedundancyType>()
            .strict()
            .nullable()
            .when('redundancy', {
                is: true,
                then: schema => schema.oneOf([RedundancyType.Primary, RedundancyType.Secondary]).required(),
                otherwise: schema => schema.oneOf(enumNumbers<RedundancyType>(RedundancyType)).optional(),
            }),
        serialPortSetting: serialPortSettingValidator()
            .nullable()
            .default(undefined)
            .when('type', {
                is: communicationTypes.serial,
                then: schema => schema.nonNullable().required(),
            }),
        lanPortSetting: lanPortSettingValidator()
            .nullable()
            .default(undefined)
            .when('type', {
                is: communicationTypes.lan,
                then: schema => schema.nonNullable().required(),
            }),
    })
        .noUnknown()
        .strict() as ObjectSchema<Port>;
}
